import React, { useEffect, useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  listenToHostCleanings,
  upsertCleaning,
  deleteCleaning,
} from "../../../../redux/cleaningSlice";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  AddCircleOutline, MoreVert, Edit, CheckCircle, Delete, PushPin,
} from "@mui/icons-material";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";

/* ── green tokens ── */
const G = {
  green:      "#16a34a",
  greenDark:  "#14532d",
  greenLight: "#dcfce7",
  border:     "#bbf7d0",
  muted:      "#6b7280",
  text:       "#14532d",
  surface:    "#ffffff",
  bg:         "#f0fdf4",
};

const getRateLabel = (rateType?: string) => {
  switch (rateType) {
    case "perHour":  return "hour";
    case "perDay":   return "day";
    case "perWeek":  return "week";
    case "perMonth": return "month";
    case "perYear":  return "year";
    default:         return "session";
  }
};

const getDiscountMultiplier = (listing: any) => {
  const d = listing.discounts;
  if (!d) return 1;
  if (d.is80) return 0.2;
  if (d.is50) return 0.5;
  if (d.is25) return 0.75;
  if (d.is10) return 0.9;
  if (d.is5)  return 0.95;
  return 1;
};

const CleaningListings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const userId           = useSelector((s: RootState) => s.auth.user?.userId || s.auth.user?.uid);
  const { listings, loading, error } = useSelector((s: RootState) => s.cleaning);
  const { rate, symbol } = useContext(CurrencyContext);

  const [menuVisible, setMenuVisible] = useState<string | null>(null);

  /* ── load host listings ── */
  useEffect(() => {
    dispatch(listenToHostCleanings());
  }, [dispatch]);

  const handleAction = async (listingId: string, action: string) => {
    if (action === "publish") {
      await dispatch(upsertCleaning({ listingId, data: { isDraft: false, inReview: true } }));
    } else if (action === "delete") {
      await dispatch(deleteCleaning(listingId));
    } else if (action === "edit") {
      navigate(`/cleaningform/${userId}/${listingId}`);
    }
    setMenuVisible(null);
  };

  const getStatus = (listing: any) => {
    if (listing.isFlagged)   return { label: "Flagged",   color: "#dc2626" };
    if (listing.isPublished) return { label: "Published", color: "#16a34a" };
    if (listing.inReview)    return { label: "In Review", color: "#ca8a04" };
    if (listing.isDraft)     return { label: "Draft",     color: "#6b7280" };
    return                          { label: "Pending",   color: "#555"    };
  };

  if (loading) return <p style={{ padding: 20, color: G.text }}>Loading listings…</p>;
  if (error)   return <p style={{ padding: 20, color: "#dc2626" }}>Error: {error}</p>;

  if (listings.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🧹</div>
        <p style={{ fontSize: 15, color: G.muted, marginBottom: 20 }}>No cleaning listings yet.</p>
        <button style={s.addButton} onClick={() => navigate(`/cleaningform/${userId}`)}>
          <AddCircleOutline style={{ marginRight: 8, fontSize: 22 }} />
          Add Listing
        </button>
      </div>
    );
  }

  return (
    <>
      <button style={s.floatingAddBtn} onClick={() => navigate(`/cleaningform/${userId}`)}>
        <AddCircleOutline style={{ marginRight: 6, fontSize: 20 }} />
        Add Listing
      </button>

      <div style={s.grid}>
        {listings.map((listing) => {
          const { label, color } = getStatus(listing);
          const discounted = (listing.rate || 0) * rate * getDiscountMultiplier(listing);

          return (
            <div key={listing.listingId} style={s.card}>
              {listing.isPromoted && listing.featuredRank && (
                <div style={s.featuredWrap}>
                  <div style={s.rankBadge}>
                    <PushPin fontSize="small" style={{ marginRight: 4 }} />
                    {listing.featuredRank.toUpperCase()}
                  </div>
                </div>
              )}

              <div
                onClick={() => navigate(`/cleaningdetails/${listing.listingId}`)}
                style={s.imageWrapper}
              >
                <img
                  src={listing.photos?.[0] || "/placeholder.jpg"}
                  alt="Listing"
                  style={s.image}
                />
                <div style={s.imageOverlay} />
              </div>

              <div style={s.cardContent}>
                <h3 style={s.title}>{listing.name || listing.title || "Untitled"}</h3>

                {Number.isFinite(listing.rate) ? (
                  <p style={s.price}>
                    {symbol}{discounted.toLocaleString(undefined, { maximumFractionDigits: 0 })} / {getRateLabel(listing.rateType)}
                  </p>
                ) : (
                  <p style={s.price}>Price N/A</p>
                )}

                <span style={{ ...s.statusBadge, background: color }}>{label}</span>
              </div>

              <div
                style={s.menuIcon}
                onClick={() => setMenuVisible(menuVisible === listing.listingId ? null : listing.listingId)}
              >
                <MoreVert fontSize="small" style={{ color: "#fff" }} />
              </div>

              {menuVisible === listing.listingId && (
                <div style={s.menu}>
                  <button style={s.menuBtn} onClick={() => handleAction(listing.listingId, "edit")}>
                    <Edit fontSize="small" style={{ marginRight: 6 }} /> Edit
                  </button>

                  {!listing.isPublished ? (
                    <button style={s.menuBtn} onClick={() => handleAction(listing.listingId, "publish")}>
                      <CheckCircle fontSize="small" style={{ marginRight: 6 }} /> Submit for Review
                    </button>
                  ) : (
                    <button style={s.menuBtnDisabled} disabled>
                      <CheckCircle fontSize="small" style={{ marginRight: 6 }} /> Published
                    </button>
                  )}

                  <button style={{ ...s.menuBtn, color: "#dc2626" }} onClick={() => handleAction(listing.listingId, "delete")}>
                    <Delete fontSize="small" style={{ marginRight: 6 }} /> Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CleaningListings;


/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 24,
    width: "100%",
  },
  card: {
    aspectRatio: "1/1",
    backgroundColor: G.greenDark,
    borderRadius: 20,
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 8px 24px rgba(22,163,74,.15)",
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${G.border}`,
  },
  featuredWrap: { position: "absolute", top: 10, left: 10, zIndex: 2 },
  rankBadge:    { background: "rgba(0,0,0,.6)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 999, display: "flex", alignItems: "center", textTransform: "uppercase" as const },
  imageWrapper: { width: "100%", flex: 1, overflow: "hidden", cursor: "pointer", position: "relative" },
  image:        { width: "100%", height: "100%", objectFit: "cover" as const },
  imageOverlay: { position: "absolute", bottom: 0, left: 0, right: 0, height: "40%", background: "linear-gradient(to top, rgba(20,83,45,.8), transparent)" },
  cardContent:  { padding: "12px 14px", backgroundColor: G.greenDark },
  title:        { fontSize: 15, fontWeight: 700, marginBottom: 4, color: "#fff" },
  price:        { fontSize: 13, color: "#4ade80", marginBottom: 4 },
  statusBadge:  { fontSize: 11, padding: "3px 8px", borderRadius: 999, display: "inline-block", color: "#fff", fontWeight: 700, marginTop: 4 },
  menuIcon:     { position: "absolute", top: 12, right: 12, backgroundColor: "rgba(0,0,0,.55)", padding: 6, borderRadius: "50%", cursor: "pointer" },
  menu:         { position: "absolute", top: 48, right: 12, backgroundColor: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,.2)", minWidth: 170, zIndex: 10 },
  menuBtn:      { display: "flex", alignItems: "center", padding: "10px 16px", fontSize: 13, background: "none", border: "none", textAlign: "left" as const, cursor: "pointer", width: "100%", color: G.greenDark },
  menuBtnDisabled: { display: "flex", alignItems: "center", padding: "10px 16px", fontSize: 13, background: "#f1f5f9", border: "none", color: "#9ca3af", cursor: "not-allowed" as const, width: "100%" },
  addButton:    { display: "inline-flex", alignItems: "center", background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  floatingAddBtn: { position: "fixed", bottom: 30, right: 40, zIndex: 1000, display: "flex", alignItems: "center", gap: 6, background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", border: "none", borderRadius: 10, padding: "12px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 6px 20px rgba(22,163,74,.4)" },
};
